/**
 * 鸿蒙分享接收（B4）：系统分享面板 → EntryAbility sendData →
 * ArkTS 深链 app://share?text=<encoded> → 本 hook 建笔记并打开
 *
 * - 未登录：暂存 sessionStorage，登录成功后补建
 * - content 组装为 ProseMirror doc JSON（与 sujiMode 默认内容同构）
 */

import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { getCurrentWindow } from '@tauri-apps/api/window'
import { useAppDispatch } from '@store'
import { createNote } from '@store/slices/notesSlice'
import { openNoteInCRDTMultiNoteEditor } from '@store/slices/crdtMultiNoteEditorSlice'
import { isWebPlatform } from '../utils/platform'
import { useAuthStore } from '../auth/store'

const SHARE_URL_PREFIX = 'app://share?text='
const PENDING_KEY = 'harmony_pending_share_text'

/** 纯文本 → ProseMirror doc JSON 字符串（按行拆段落） */
function buildDocContent(text: string): string {
  const lines = text.split('\n')
  const paragraphs = lines.map((line) => {
    const t = line.replace(/\r$/, '')
    if (t.trim() === '') {
      return { type: 'paragraph' }
    }
    return { type: 'paragraph', content: [{ type: 'text', text: t }] }
  })
  if (paragraphs.length === 0) {
    paragraphs.push({ type: 'paragraph' })
  }
  return JSON.stringify({ type: 'doc', content: paragraphs })
}

/** 标题：首个非空行截 40 字符，兜底"分享的笔记" */
function deriveTitle(text: string): string {
  const firstLine = text
    .split('\n')
    .map((l) => l.trim())
    .find((l) => l !== '') ?? ''
  const title = firstLine.slice(0, 40)
  return title !== '' ? title : '分享的笔记'
}

export function useHarmonyShareReceive(): void {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const creatingRef = useRef(false)

  const consumeShare = async (rawText: string) => {
    if (creatingRef.current) return
    creatingRef.current = true
    try {
      const note = await dispatch(
        createNote({
          title: deriveTitle(rawText),
          content: buildDocContent(rawText),
          isFolder: false,
        }),
      ).unwrap()
      if (note?.id) {
        dispatch(openNoteInCRDTMultiNoteEditor(note.id))
        navigate(`/crdt/notes/${note.id}`)
      }
    } catch (error) {
      console.error('[HarmonyShare] 分享内容建笔记失败:', error)
    } finally {
      creatingRef.current = false
    }
  }

  useEffect(() => {
    if (isWebPlatform()) return

    let disposed = false
    let unlisten: (() => void) | null = null

    getCurrentWindow()
      .listen('deep-link://new-url', (event) => {
        const url = event.payload as string
        if (typeof url !== 'string' || !url.startsWith(SHARE_URL_PREFIX)) return
        const text = decodeURIComponent(url.slice(SHARE_URL_PREFIX.length))
        if (!text) return
        if (!useAuthStore.getState().isAuthenticated) {
          sessionStorage.setItem(PENDING_KEY, text)
          return
        }
        consumeShare(text)
      })
      .then((fn) => {
        if (disposed) fn()
        else unlisten = fn
      })

    return () => {
      disposed = true
      unlisten?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 未登录时暂存的分享文本，登录成功后补建
  useEffect(() => {
    if (!isAuthenticated) return
    const pending = sessionStorage.getItem(PENDING_KEY)
    if (!pending) return
    sessionStorage.removeItem(PENDING_KEY)
    consumeShare(pending)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])
}
